import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpCircle, Copy, FileSearch, FileSpreadsheet, FileText, GripVertical, MoreHorizontal, Pencil, Sparkles, Store, Trash2 } from "lucide-react";
import type { InstalledFrom, ResultEnvelope, Widget } from "@cd/core";
import { canPick, canZoomInto, groupLabel, type PickedValue } from "@cd/viz";
import { Card, Tip } from "../../components/ui/primitives";
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, Sheet } from "../../components/ui/overlay";
import { Provenance, TrustBadge, WarningList } from "../../components/viz/Trust";
import type { OnPick } from "../../components/viz/onPick";
import { useWidgetQuery, VizBody, WidgetState } from "../../components/viz/WidgetView";
import { api } from "../../lib/api";
import { shownRange, zoomQuery, zoomTo, type DateZoom, type ZoomGrain } from "../../lib/dateZoom";
import { stepFrom, type DrillStep } from "../../lib/drill";
import { chipsFor, toDashboardFilters, type addPageFilter, type PageFilter } from "../../lib/pageFilters";
import type { TimeSelection } from "../../lib/time";
import { cn } from "../../lib/utils";
import { PageFilterBadges } from "./PageFilterBadges";
import { PickMenu } from "./PickMenu";

/**
 * Records the download for the audit log, then builds the file. The file comes either way: a
 * failed record reaches the worker logs (and the console here), never the person downloading.
 */
function download(widget: Widget, result: ResultEnvelope, format: "xlsx" | "csv", ctx: { dashboardId?: string; locationIds?: string[] }) {
  const def = widget.definition;
  void api.recordDownload({
    widgetId: widget.id, dashboardId: ctx.dashboardId, title: def.title, format, rowCount: result.rows.length,
    vizType: def.visualization.type, locationIds: ctx.locationIds, timeRange: result.provenance.timeRange,
    filters: result.provenance.filters.map((f) => f.slice(0, 600)),
  }).catch((e) => console.warn("[download] not recorded", e));
  // Loaded on click, so the zip writer stays out of the dashboard bundle.
  void import("../../lib/export").then((m) => m.downloadWidget(def.visualization, result, def.title, format));
}

const UPDATE_BADGE = "inline-flex shrink-0 items-center gap-1 rounded-md bg-warn-bg px-1.5 py-0.5 text-[11px] font-medium leading-4 text-warn-fg hover:underline";

interface Props {
  widget: Widget;
  time: TimeSelection;
  locationIds?: string[];
  /** The dashboard's input values (saved, or this viewer's); absent: the widget's defaults. */
  inputs?: Record<string, number>;
  /** The dashboard it's on, for the download log; absent in a marketplace preview. */
  dashboardId?: string;
  canEdit: boolean;
  onEdit: () => void;
  /** Opens the builder on it with Ask on top; unset for people without Ask, or a widget Ask can't read. */
  onAskEdit?: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  /** Publish this widget to the marketplace (owners only). */
  onPublish?: () => void;
  /** The page's drill-down filters, applied where this widget can take them. */
  pageFilters?: PageFilter[];
  /** Adds a page filter from a clicked value; without it, values can't be clicked. */
  onAddFilter?: (f: Parameters<typeof addPageFilter>[1]) => void;
  /** The page's date zoom. `time` is already its range; this steps time axes one grain finer. */
  zoom?: DateZoom;
  /** Zooms the page into a clicked date bucket; without it, dates can't be clicked. */
  onZoom?: (z: DateZoom) => void;
  /** Opens "Break down … by…" from a clicked value; without it, the menu doesn't offer it. */
  onBreakDown?: (step: DrillStep) => void;
  /** A time field's allowed grains, for stepping finer while zoomed; absent until they're known. */
  grainsOf?: (pack: string | undefined, ref: string) => readonly string[] | undefined;
  /** Where it was installed from, when a newer version is out. */
  update?: InstalledFrom;
  /** Update it to that version (owners only). */
  onUpdate?: () => void;
}

export function WidgetCard({
  widget, time, locationIds, inputs, dashboardId, canEdit, onEdit, onAskEdit, onDuplicate, onRemove, onPublish, pageFilters, onAddFilter, zoom, onZoom, onBreakDown, grainsOf, update, onUpdate,
}: Props) {
  const def = widget.definition;
  // A chip on a field this widget is grouped by highlights instead of filtering (see chipsFor).
  const { sent, highlight } = useMemo(() => chipsFor(def.query, pageFilters ?? []), [pageFilters, def.query]);
  const dashboardFilters = useMemo(() => (sent.length ? toDashboardFilters(sent) : undefined), [sent]);
  // The dashboard's range replaces any range saved on the widget.
  // While zoomed, a widget with a time axis waits for its grains rather than querying twice.
  const query = useMemo(() => {
    const q = { ...def.query, timeRange: undefined, timePreset: undefined, ...time };
    if (!zoom || !q.dimensions?.some((d) => d.grain)) return q;
    return grainsOf ? zoomQuery(q, zoom, (ref) => grainsOf(def.query.pack, ref)) : null;
  }, [def.query, time, zoom, grainsOf]);
  const q = useWidgetQuery(query, locationIds, { source: "widget", widgetId: widget.id }, dashboardFilters, inputs);
  const [menu, setMenu] = useState<{ at: { x: number; y: number }; picks: PickedValue[] } | null>(null);
  const onPick: OnPick | undefined = (onAddFilter || onZoom || onBreakDown) && ((picks, at) => {
    const usable = picks.filter((p) =>
      canPick(p.column) && (canZoomInto(p.column) ? (!!onZoom || !!onBreakDown) && !!zoomFor(p) : !!onAddFilter || !!onBreakDown));
    if (usable.length) setMenu({ at, picks: usable });
  });
  // A date bucket zooms to the days of it this widget showed, so the number clicked stays the same.
  const zoomFor = (p: PickedValue) => (canZoomInto(p.column) ? zoomTo(p.value, p.column.grain as ZoomGrain, shownRange(q.data)) : undefined);
  const [work, setWork] = useState(false);
  // A single number has nothing to tabulate; every other widget is a table underneath.
  const canExport = def.visualization.type !== "kpi" && !!q.data?.rows.length && !q.error;
  const warn = q.data?.warnings.filter((w) => w.severity === "warn") ?? [];

  return (
    <Card className="group flex size-full flex-col overflow-hidden">
      <div className="flex items-center gap-1.5 px-3.5 pt-3 pb-1">
        {canEdit && (
          <GripVertical className="drag-handle -ml-1.5 size-4 shrink-0 cursor-grab text-subtle opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing" aria-label="Drag to move" />
        )}
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium" title={def.title}>{def.title}</h3>
        {update && (
          <Tip content={`You have v${update.version} from the marketplace; v${update.latestVersion} is out`}>
            {canEdit && onUpdate ? (
              <button onClick={onUpdate} className={UPDATE_BADGE}>
                <ArrowUpCircle className="size-3" /> Update to v{update.latestVersion}
              </button>
            ) : (
              <Link to="/marketplace/$id" params={{ id: update.listingId }} className={UPDATE_BADGE}>
                <ArrowUpCircle className="size-3" /> v{update.latestVersion} available
              </Link>
            )}
          </Tip>
        )}
        <PageFilterBadges result={q.data} sent={sent} pageFilters={pageFilters} />
        {q.data && <TrustBadge certified={q.data.certified} compact />}
        <Menu>
          <MenuTrigger className="rounded-md p-1 text-subtle opacity-0 transition-opacity hover:bg-surface-2 hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100" aria-label="Widget menu">
            <MoreHorizontal className="size-4" />
          </MenuTrigger>
          <MenuContent>
            <MenuItem onSelect={() => setWork(true)}><FileSearch className="size-4" /> Show your work</MenuItem>
            {canExport && q.data && (
              <>
                <MenuItem onSelect={() => download(widget, q.data!, "xlsx", { dashboardId, locationIds })}><FileSpreadsheet className="size-4" /> Download Excel (.xlsx)</MenuItem>
                <MenuItem onSelect={() => download(widget, q.data!, "csv", { dashboardId, locationIds })}><FileText className="size-4" /> Download CSV</MenuItem>
              </>
            )}
            {canEdit && (
              <>
                <MenuItem onSelect={onEdit}><Pencil className="size-4" /> Edit</MenuItem>
                {onAskEdit && <MenuItem onSelect={onAskEdit}><Sparkles className="size-4" /> Change with Ask…</MenuItem>}
                <MenuItem onSelect={onDuplicate}><Copy className="size-4" /> Duplicate</MenuItem>
                {onPublish && <MenuItem onSelect={onPublish}><Store className="size-4" /> Publish widget</MenuItem>}
                <MenuSeparator />
                <MenuItem danger onSelect={onRemove}><Trash2 className="size-4" /> Remove from dashboard</MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>
      </div>
      <div className={cn("relative min-h-0 flex-1 px-3.5 pb-3", q.isFetching && q.data && "opacity-70 transition-opacity")}>
        <WidgetState query={q} />
        {q.data && !q.error && <VizBody viz={def.visualization} result={q.data} onPick={onPick} highlight={highlight} />}
      </div>
      {onPick && (
        <PickMenu
          at={menu?.at ?? null}
          picks={menu?.picks ?? []}
          onClose={() => setMenu(null)}
          onFilter={(p, exclude) => {
            setMenu(null);
            onAddFilter?.({ pack: def.query.pack, ref: p.column.key, label: p.column.label, exclude, value: p.value, valueLabel: groupLabel(p.column, p.value) });
          }}
          canFilter={!!onAddFilter}
          zoomFor={(p) => (onZoom ? zoomFor(p) : undefined)}
          onZoom={(z) => {
            setMenu(null);
            onZoom?.(z);
          }}
          breakDown={onBreakDown && stepFrom(menu?.picks ?? [], shownRange(q.data))}
          onBreakDown={(step) => {
            setMenu(null);
            onBreakDown?.(step);
          }}
        />
      )}
      {warn.length > 0 && (
        <button onClick={() => setWork(true)} className="mx-3 mb-3 text-left">
          <WarningList warnings={warn.slice(0, 1)} dense />
        </button>
      )}
      <Sheet open={work} onOpenChange={setWork} title={def.title}>
        {q.data && <Provenance result={q.data} filterLabels={Object.fromEntries(pageFilters?.map((c) => [c.ref, c.label]) ?? [])} />}
      </Sheet>
    </Card>
  );
}
